import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useClaimsUnreadCount } from '../../hooks/useClaimsUnreadCount';
import { useMyClaimNotifications } from '../../hooks/useClaimNotifications';
import { toVocative } from '../../utils/vocative';
import { canView } from '../../utils/access';
import { NameLanguageSelect } from './NameLanguageSelect';
import type { ClaimNotification } from '../../types/claims';
import type { ModuleKey, User, UserRole } from '../../types/api';

// ── Navigation structure ──────────────────────────────────────────────────────

type NavChild = { label: string; path: string; badgeKey?: 'claims'; adminOnly?: boolean };
type NavGroup = {
  key: string;
  label: string;
  module: ModuleKey;
  items: NavChild[];
  /** Група з довгим списком: у сайдбарі показується згорнутою. */
  collapsible?: boolean;
};

const NAV: NavGroup[] = [
  {
    key: 'overview',
    label: 'Огляд',
    module: 'reports',
    items: [{ label: 'Дашборд', path: '/dashboard' }],
  },
  {
    key: 'purchases',
    label: 'Закупівлі',
    module: 'purchases',
    items: [
      { label: 'Круглі лісоматеріали', path: '/purchases/round' },
      { label: 'Сира деревина', path: '/purchases/raw' },
      { label: 'Суха деревина', path: '/purchases/dry' },
      { label: 'Фурнітура', path: '/purchases/hardware' },
    ],
  },
  {
    key: 'production',
    label: 'Виробництво',
    module: 'production',
    items: [
      { label: 'Розпил круглих лісоматеріалів', path: '/sawing' },
      { label: 'Сушіння деревини', path: '/drying' },
      { label: 'Атмосферна сушка', path: '/atmospheric-drying' },
      { label: 'Сортування дошки', path: '/sorting' },
      { label: 'Переробка в ЧЗ', path: '/processing' },
      // Цехи стоять у порядку маршруту виробу — так їх і читає зміна.
      { label: 'Модуль заготівельний', path: '/production/prep' },
      { label: 'Модуль форматування', path: '/production/format' },
      { label: 'Модуль столярний', path: '/production/joinery' },
      { label: 'Модуль стільцевий', path: '/production/chair' },
      { label: 'Модуль пакування', path: '/production/packing' },
    ],
  },
  {
    key: 'warehouses',
    label: 'Склад підприємства',
    collapsible: true,
    module: 'stock',
    items: [
      { label: 'Склад круглих лісоматеріалів', path: '/warehouses/round-timber' },
      { label: 'Підсушна дільниця', path: '/warehouses/predrying' },
      { label: 'На сортуванні', path: '/warehouses/sorting' },
      { label: 'Склад сухої деревини', path: '/warehouses/dry' },
      { label: 'Заготівельний цех', path: '/warehouses/prep-shop' },
      { label: 'Склад чорнових заготовок', path: '/warehouses/blanks' },
      { label: 'Склад готової продукції', path: '/warehouses/products' },
      { label: 'Склад дров', path: '/warehouses/firewood' },
      { label: 'Склад відходів', path: '/warehouses/waste' },
      { label: 'Склад фурнітури', path: '/warehouses/hardware' },
      { label: 'Переміщення', path: '/warehouses/transfers' },
      { label: 'Потреба в ЧЗ', path: '/warehouses/blank-demand' },
      { label: 'Потреба у фурнітурі', path: '/warehouses/hardware-demand' },
      { label: 'Коригування залишків', path: '/warehouses/adjustments', adminOnly: true },
    ],
  },
  {
    key: 'marketing',
    label: 'Маркетинг',
    module: 'marketing',
    items: [
      { label: 'Планування замовлень', path: '/orders' },
      { label: 'Майстер-замовлення', path: '/master-orders' },
      { label: 'Відвантаження', path: '/shipments' },
      { label: 'Рекламації', path: '/claims', badgeKey: 'claims' },
    ],
  },
  {
    key: 'finance',
    label: 'Фінанси',
    module: 'invoices',
    items: [{ label: 'Рахунки до оплати', path: '/invoices' }],
  },
  {
    key: 'nomenclature',
    label: 'Номенклатура',
    collapsible: true,
    module: 'references',
    items: [
      { label: 'Круглі лісоматеріали', path: '/references/round-timber' },
      { label: 'Сира деревина', path: '/references/raw-materials' },
      { label: 'Суха деревина', path: '/references/dry-materials' },
      { label: 'Класифікація дошки', path: '/references/board-sort-classes' },
      { label: 'Чорнові заготовки', path: '/references/blanks' },
      { label: 'Фурнітура', path: '/references/hardware' },
      { label: 'Наша продукція', path: '/references/products' },
      { label: 'Оздоблення', path: '/references/finishes' },
      { label: 'Сортність', path: '/references/product-grades' },
      { label: 'Тканини / шкіра', path: '/references/fabrics' },
      { label: 'Матеріали', path: '/references/materials' },
      { label: 'Постачальники', path: '/references/suppliers' },
      { label: 'Покупці', path: '/references/customers' },
      { label: 'Сушильні камери', path: '/references/drying-chambers' },
      { label: 'Види відходів', path: '/references/waste-types' },
      { label: 'Норми виробів', path: '/norms' },
    ],
  },
  {
    key: 'reports',
    label: 'Звіти',
    module: 'reports',
    items: [
      { label: 'Журнал операцій', path: '/reports/movements' },
      { label: 'Користувачі та права', path: '/users', adminOnly: true },
    ],
  },
];

/** Filter nav by the user's module access; drop admin-only items for non-admins. */
function visibleNav(user: User | null): NavGroup[] {
  const result: NavGroup[] = [];
  for (const group of NAV) {
    if (!canView(user, group.module)) continue;
    const items = group.items.filter((it) => !it.adminOnly || user?.is_admin);
    if (items.length === 0) continue;
    result.push({ ...group, items });
  }
  return result;
}

// ── Page titles shown in the header ──────────────────────────────────────────

const PAGE_TITLES: { prefix: string; title: string }[] = [
  { prefix: '/dashboard', title: 'Огляд підприємства' },
  // Точніший шлях мусить стояти перед загальним: пошук іде по першому збігу.
  { prefix: '/purchases/hardware', title: 'Закупівля фурнітури' },
  { prefix: '/purchases', title: 'Закупівля деревини' },
  { prefix: '/sawing', title: 'Розпил круглих лісоматеріалів' },
  { prefix: '/atmospheric-drying', title: 'Атмосферна сушка' },
  { prefix: '/drying', title: 'Сушіння деревини' },
  { prefix: '/sorting', title: 'Сортування дошки' },
  { prefix: '/processing', title: 'Переробка в ЧЗ — Виготовлення чорнової заготовки' },
  { prefix: '/production/prep', title: 'Модуль заготівельний' },
  { prefix: '/production/format', title: 'Модуль форматування' },
  { prefix: '/production/joinery', title: 'Модуль столярний' },
  { prefix: '/production/chair', title: 'Модуль стільцевий' },
  { prefix: '/production/packing', title: 'Модуль пакування' },
  { prefix: '/orders', title: 'Планування замовлень' },
  { prefix: '/master-orders', title: 'Майстер-замовлення' },
  { prefix: '/shipments', title: 'Відвантаження' },
  { prefix: '/claims', title: 'Рекламації' },
  { prefix: '/invoices', title: 'Рахунки до оплати' },
  { prefix: '/warehouses/products', title: 'Склад готової продукції' },
  { prefix: '/warehouses/transfers', title: 'Переміщення' },
  { prefix: '/warehouses/adjustments', title: 'Коригування залишків' },
  { prefix: '/warehouses/blank-demand', title: 'Потреба в заготовках (ЧЗ)' },
  { prefix: '/warehouses/hardware-demand', title: 'Потреба у фурнітурі' },
  { prefix: '/warehouses', title: 'Склад підприємства' },
  { prefix: '/references', title: 'Номенклатура' },
  { prefix: '/norms', title: 'Норми виробів' },
  { prefix: '/reports/movements', title: 'Журнал операцій' },
  { prefix: '/users', title: 'Користувачі та права' },
];

function pageTitle(pathname: string): string {
  const hit = PAGE_TITLES.find((t) => pathname.startsWith(t.prefix));
  return hit?.title ?? 'DNW ERP';
}

const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Адміністратор',
  manager: 'Менеджер',
  warehouse: 'Комірник',
  viewer: 'Перегляд',
};

const HEADER_DATE_FMT: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
};

/** `ср, 29 липня 2026 р.` → `Ср, 29 липня 2026` — як у прототипі. */
function headerDate(d: Date): string {
  const s = d.toLocaleDateString('uk-UA', HEADER_DATE_FMT).replace(/\s*р\.$/, '');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ── Sidebar ───────────────────────────────────────────────────────────────────

const SIDEBAR_WIDTH = 248;
// Згорнута панель пам'ятається в браузері: хто працює в одному розділі
// (наприклад, лише з рахунками), згортає її раз і більше про неї не думає.
const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';
const HEADER_HEIGHT = 58;
const CONTENT_MIN_WIDTH = 980;

function Brand({ onClick }: { onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      style={{
        height: HEADER_HEIGHT,
        boxSizing: 'border-box',
        padding: '0 20px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        borderBottom: '1px solid var(--border-sidebar)',
        cursor: 'pointer',
        userSelect: 'none',
        flexShrink: 0,
      }}
    >
      <div className="wordmark" style={{ fontSize: 27, lineHeight: 1.05, color: 'var(--text)' }}>
        Dniprowood
      </div>
      <div
        style={{
          fontFamily: 'var(--font-script)',
          fontWeight: 700,
          fontSize: 26,
          color: 'var(--brand)',
          letterSpacing: '0.01em',
          lineHeight: 1,
          marginTop: -2,
          whiteSpace: 'nowrap',
        }}
      >
        enterprise resource planning
      </div>
    </div>
  );
}

function NavLink({
  label,
  isActive,
  badgeCount,
  onClick,
}: {
  label: string;
  isActive: boolean;
  badgeCount: number;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        padding: '7px 10px',
        borderRadius: 8,
        cursor: 'pointer',
        fontSize: 13,
        transition: 'background 140ms ease',
        background: isActive ? 'var(--brand-bg)' : hovered ? '#DBD5C7' : 'transparent',
        color: isActive ? 'var(--brand-hover)' : hovered ? 'var(--text)' : 'var(--text-secondary)',
        fontWeight: isActive ? 600 : 500,
      }}
    >
      <span
        style={{
          width: 3,
          height: 16,
          borderRadius: 2,
          background: isActive ? 'var(--brand)' : 'transparent',
          flexShrink: 0,
        }}
      />
      <span
        style={{
          flex: 1,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </span>
      {badgeCount > 0 && (
        <span
          style={{
            background: 'var(--badge)',
            color: 'var(--text)',
            borderRadius: 999,
            fontSize: 10.5,
            fontWeight: 700,
            padding: '1px 7px',
            lineHeight: 1.5,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {badgeCount > 99 ? '99+' : badgeCount}
        </span>
      )}
    </div>
  );
}

function UserCard({
  fullName,
  role,
  initial,
  onLogout,
}: {
  fullName: string;
  role: string;
  initial: string;
  onLogout: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{
        padding: '12px 14px',
        borderTop: '1px solid var(--border-sidebar)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: 'var(--brand)',
          color: '#FBF8F2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 600,
          fontSize: 13,
          flexShrink: 0,
        }}
      >
        {initial}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: 13,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {fullName}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{role}</div>
        <NameLanguageSelect />
      </div>
      <span
        onClick={onLogout}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        title="Вийти з системи"
        style={{
          color: hovered ? 'var(--error)' : 'var(--text-disabled)',
          background: hovered ? 'var(--error-bg)' : 'transparent',
          fontSize: 14,
          cursor: 'pointer',
          padding: '4px 6px',
          borderRadius: 6,
        }}
      >
        ⏻
      </span>
    </div>
  );
}

// ── AppLayout ─────────────────────────────────────────────────────────────────

const AppLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Групи з довгим списком (Номенклатура) за замовчуванням згорнуті —
  // розкриваються кліком по заголовку або самі, якщо відкрита їхня сторінка.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1',
  );
  const toggleSidebar = () =>
    setCollapsed((value) => {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, value ? '0' : '1');
      return !value;
    });

  const { data: claimsUnread = 0 } = useClaimsUnreadCount();
  const { data: myNotifications = [] } = useMyClaimNotifications();
  const badgeCounts: Record<string, number> = { claims: claimsUnread };

  const fullName = user?.full_name ?? user?.email ?? 'Користувач';
  const initial = fullName.charAt(0).toUpperCase();
  const roleLabel = user ? (ROLE_LABELS[user.role] ?? 'Користувач') : 'Користувач';
  const today = headerDate(new Date());

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* ── Sidebar ── */}
      <aside
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          bottom: 0,
          width: SIDEBAR_WIDTH,
          background: 'var(--bg-sidebar)',
          borderRight: '1px solid var(--border-sidebar)',
          display: collapsed ? 'none' : 'flex',
          flexDirection: 'column',
          zIndex: 100,
        }}
      >
        <Brand onClick={() => navigate('/dashboard')} />

        <nav style={{ flex: 1, overflowY: 'auto', padding: '10px 12px 12px' }}>
          {visibleNav(user).map((group) => {
            const hasActive = group.items.some((i) => i.path === location.pathname);
            const open = !group.collapsible || openGroups[group.key] || hasActive;
            return (
              <div key={group.key} style={{ marginBottom: 6 }}>
                <div
                  onClick={
                    group.collapsible
                      ? () => setOpenGroups((g) => ({ ...g, [group.key]: !g[group.key] }))
                      : undefined
                  }
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '12px 10px 6px',
                    cursor: group.collapsible ? 'pointer' : 'default',
                    userSelect: 'none',
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.09em',
                      textTransform: 'uppercase',
                      color: '#5A5344',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {group.label}
                  </span>
                  {group.collapsible && (
                    <span style={{ fontSize: 9, color: '#8A8272', lineHeight: 1 }}>
                      {open ? '▲' : '▼'}
                    </span>
                  )}
                  <span style={{ flex: 1, height: 1, background: '#D3CCBC' }} />
                  {group.collapsible && !open && (
                    <span
                      style={{
                        fontSize: 10.5,
                        color: '#8A8272',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {group.items.length}
                    </span>
                  )}
                </div>
                {open &&
                  group.items.map((item) => (
                    <NavLink
                      key={item.path}
                      label={item.label}
                      isActive={location.pathname === item.path}
                      badgeCount={item.badgeKey ? (badgeCounts[item.badgeKey] ?? 0) : 0}
                      onClick={() => navigate(item.path)}
                    />
                  ))}
              </div>
            );
          })}
        </nav>

        <UserCard fullName={fullName} role={roleLabel} initial={initial} onLogout={logout} />
      </aside>

      {/* ── Main ── */}
      <div
        style={{
          marginLeft: collapsed ? 0 : SIDEBAR_WIDTH,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 90,
            height: HEADER_HEIGHT,
            boxSizing: 'border-box',
            background: 'var(--header-bg)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            padding: '0 28px',
            minWidth: CONTENT_MIN_WIDTH,
          }}
        >
          <button
            type="button"
            onClick={toggleSidebar}
            title={collapsed ? 'Показати меню' : 'Сховати меню'}
            aria-label={collapsed ? 'Показати меню' : 'Сховати меню'}
            style={{
              border: '1px solid var(--border-field)',
              background: 'var(--surface)',
              borderRadius: 6,
              width: 30,
              height: 30,
              fontSize: 15,
              lineHeight: 1,
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            {collapsed ? '☰' : '«'}
          </button>
          <div style={{ fontWeight: 700, fontSize: 17, letterSpacing: '-0.015em' }}>
            {pageTitle(location.pathname)}
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', fontWeight: 500 }}>{today}</div>
        </header>

        <main style={{ padding: '24px 28px 40px', minWidth: CONTENT_MIN_WIDTH }}>
          <Outlet />
        </main>
      </div>

      <NotificationStack
        notifications={myNotifications}
        userFirstName={user?.first_name}
        onOpen={(n) => navigate(`/claims?open=${n.claim}`)}
      />
    </div>
  );
};

// ── Notification stack: floating cards, bottom-right ─────────────────────────

function NotificationStack({
  notifications,
  userFirstName,
  onOpen,
}: {
  notifications: ClaimNotification[];
  userFirstName: string | undefined;
  onOpen: (n: ClaimNotification) => void;
}) {
  if (notifications.length === 0) return null;
  const greeting = userFirstName ? toVocative(userFirstName) : 'Колего';

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 24,
        right: 26,
        zIndex: 200,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        width: 336,
      }}
    >
      {notifications.map((n) => (
        <NotificationCard
          key={n.id}
          notification={n}
          greeting={greeting}
          onOpen={() => onOpen(n)}
        />
      ))}
    </div>
  );
}

function NotificationCard({
  notification,
  greeting,
  onOpen,
}: {
  notification: ClaimNotification;
  greeting: string;
  onOpen: () => void;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onOpen}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: 'var(--badge-bg)',
        border: '1px solid var(--badge)',
        borderRadius: 12,
        padding: '12px 14px',
        cursor: 'pointer',
        boxShadow: hovered
          ? '0 12px 28px rgba(245, 197, 24, 0.3)'
          : '0 6px 18px rgba(245, 197, 24, 0.22)',
        transition: 'box-shadow 200ms ease',
        animation: 'notifSlideIn 360ms cubic-bezier(0.32, 0.72, 0.24, 1)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: '#92400E',
              marginBottom: 4,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {notification.claim_code} · {notification.claim_company}
          </div>
          <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text)', lineHeight: 1.35 }}>
            {greeting}, вас залучено до обговорення рекламації — дайте відповідь
          </div>
          {notification.asked_by_name && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 5 }}>
              Залучив(ла): <span style={{ fontWeight: 600 }}>{notification.asked_by_name}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AppLayout;
